import { Environments } from "../environmentInfo/Constants";
import { BETA_ACCOUNT_ID, SAAM_TEST_USER_BETA, SAAM_TEST_USER_GAMMA } from "./constants";
import { Role, User } from "./types/interfaces";

/**
 * SAAM Test User Configuration
 *
 * These are the LDAP system accounts created for SAAM authentication testing.
 * They are linked to IAM roles in different AWS accounts for environment-specific testing.
 *
 * BETA Environment: svc-mw-788277276912-ui-tests (linked to arn:aws:iam::788277276912:role/ui-tests)
 * GAMMA Environment: svc-mw-567981344308-ui-tests (linked to arn:aws:iam::567981344308:role/ui-tests)
 */
export const SAAM_TEST_USERS = [
  SAAM_TEST_USER_BETA, // BETA environment SAAM test user
  SAAM_TEST_USER_GAMMA, // GAMMA environment SAAM test user
];

/**
 * Checks if a given username is a SAAM test user
 * @param username - The username to check
 * @returns true if the username is a SAAM test user, false otherwise
 */
export const isSaamTestUser = (username: string): boolean => {
  return SAAM_TEST_USERS.includes(username as (typeof SAAM_TEST_USERS)[number]);
};

/**
 * Creates a complete User object for SAAM test users with full permissions
 *
 * SAAM test users are granted all available roles for comprehensive testing:
 * - Workers Compensation claims (both user and PM levels)
 * - Cargo claims
 * - IPL (International Product Liability) claims
 *
 * @param username - The SAAM test username
 * @returns User object with full permissions for testing
 */
export const createSaamTestUser = (username: string): User => {
  // Extract environment from username for better identification
  const environment = username.includes(BETA_ACCOUNT_ID) ? Environments.BETA : Environments.GAMMA;

  return {
    id: username,
    givenName: "SAAM",
    familyName: `Test User (${environment})`,
    email: `${username}@amazon.com`,
    roles: [
      Role.WC_CLAIM_USER, // Workers Compensation user access
      Role.WC_CLAIM_PRODUCT_GROUP, // Workers Compensation PM access
      Role.CARGO_CLAIM_USER, // Cargo claims access
      Role.IPL_CLAIM_USER, // IPL claims access
      Role.WC_CLAIM_CANADA_USER, // Workers Compensation Canada access
    ],
    siteCode: "TEST", // Generic site code for testing
    managerAlias: "test-manager",
    jobDescription: "Automated UI Testing Account",
  };
};

/**
 * Determines if a user has SAAM test user permissions
 * This can be used in authorization checks throughout the application
 *
 * @param user - User object to check
 * @returns true if user has SAAM test permissions, false otherwise
 */
export const hasSaamTestPermissions = (user: User): boolean => {
  return isSaamTestUser(user.id);
};

/**
 * Gets all roles that SAAM test users should have
 * Useful for permission checks and role validation
 *
 * @returns Array of all roles granted to SAAM test users
 */
export const getSaamTestUserRoles = (): Role[] => {
  return [
    Role.WC_CLAIM_USER,
    Role.WC_CLAIM_PRODUCT_GROUP,
    Role.CARGO_CLAIM_USER,
    Role.IPL_CLAIM_USER,
    Role.WC_CLAIM_CANADA_USER,
  ];
};
